import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveFileInBackground } from '../lib/saveFile';

export interface InvoiceData {
  id: string;
  number: string;
  billTo: { name: string; organisation: string; email: string };
  items: { description: string; amount: number }[];
  total: number;
  currency: string;
  method: string;
  paidAt: string | null;
  periodFrom: string | null;
  periodTo: string | null;
  seller: { name: string; uen: string; product: string };
}

const money = (n: number, c: string) => `${c === 'SGD' ? 'S$' : `${c} `}${n.toFixed(2)}`;
const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

/**
 * A paid invoice as a PDF, drawn from the server's record. Blisx is not
 * GST-registered, so it is titled "Invoice" (not "Tax invoice") and shows no GST.
 */
export function downloadInvoicePdf(inv: InvoiceData) {
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const left = 56;
  let y = 64;

  pdf.setFont('helvetica', 'bold').setFontSize(22).text('Invoice', left, y);
  pdf.setFont('helvetica', 'normal').setFontSize(10).setTextColor(90);
  pdf.text(inv.number, left, (y += 18));

  pdf.setTextColor(20).setFont('helvetica', 'bold').setFontSize(11);
  pdf.text(inv.seller.name, 540, 64, { align: 'right' });
  pdf.setFont('helvetica', 'normal').setFontSize(10).setTextColor(90);
  pdf.text(`UEN ${inv.seller.uen}`, 540, 80, { align: 'right' });
  pdf.text(inv.seller.product, 540, 94, { align: 'right' });

  y += 40;
  pdf.setTextColor(90).text('Billed to', left, y);
  pdf.setTextColor(20);
  const billTo = [inv.billTo.name, inv.billTo.organisation, inv.billTo.email].filter(Boolean);
  billTo.forEach((line, i) => pdf.text(line, left, y + 16 + i * 14));

  const facts: [string, string][] = [
    ['Date paid', day(inv.paidAt)],
    ...(inv.periodFrom && inv.periodTo
      ? ([['Service period', `${day(inv.periodFrom)} – ${day(inv.periodTo)}`]] as [string, string][])
      : []),
    ['Payment', inv.method || 'HitPay'],
    ['Status', 'Paid'],
  ];
  facts.forEach(([k, v], i) => {
    pdf.setTextColor(90).text(k, 330, y + i * 16);
    pdf.setTextColor(20).text(v, 540, y + i * 16, { align: 'right' });
  });

  y += Math.max(billTo.length * 14 + 16, facts.length * 16) + 30;
  autoTable(pdf, {
    startY: y,
    margin: { left, right: 56 },
    head: [['Description', 'Amount']],
    body: inv.items.map((i) => [i.description, money(i.amount, inv.currency)]),
    foot: [['Total paid', money(inv.total, inv.currency)]],
    theme: 'plain',
    headStyles: { fontStyle: 'bold', textColor: 90, lineWidth: { bottom: 0.5 }, lineColor: 180 },
    footStyles: { fontStyle: 'bold', textColor: 20, lineWidth: { top: 1 }, lineColor: 20 },
    columnStyles: { 1: { halign: 'right', cellWidth: 110 } },
    styles: { fontSize: 10, cellPadding: 6 },
  });

  const end = (pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y + 80;
  pdf.setFontSize(9).setTextColor(110);
  pdf.text(`Reference ${inv.id}`, left, end + 30);
  pdf.text('Thank you for using BlisxBench.', left, end + 44);

  saveFileInBackground(pdf.output('blob'), `${inv.number}.pdf`);
}
